import React from 'react';
import { useEffect, useState } from 'react';
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import theme from "../../../assets/theme";
import HeaderBar from "./component/HeaderBar";
import OrderList from "./component/OrderList";
import dataTableData from "./data/dataTableData";
import { Order } from '../../../types/orderTypes';
import OrdersController from '../../../controllers/orderController'
import { Alert } from '@mui/material';
import { nextStep, READY_TO_SHIP_REMINDER } from '../../../components/OrderShipActions';

// The status a row shows. Also what the status filter matches on, so the two always agree.
const shownStatus = (order: any): string => {
    const itemStatus = order.items?.[0]?.status ?? '';
    const isException = /cancel|return|refund/i.test(itemStatus);
    return String(isException ? itemStatus : (order.status ?? itemStatus ?? '-'));
};

function Orders() {
    const { typography, palette } = theme;
    const [orders, setOrders] = useState<Order[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [tableData, setTableData] = useState<any>(null);
    const [filters, setFilters] = useState<any>({});
    // Bumped after Approve / Ready to Ship so the table shows the new state.
    const [reloadTick, setReloadTick] = useState(0);

    useEffect(() => {
        const loadOrders = async () => {
            try {
                // Get orders from the API
                // 100 is the most the API returns in one page. The filters below work on
                // what is loaded, so loading only the newest ten made them look broken.
                const response = await OrdersController.getMerchantOrders(0, 100);
                console.log(response.data.data, ' here is the response');
                
                // Transform the API response data to match the expected format for OrderList
                const transformedData = transformOrderData(response.data.data);
                setTableData(transformedData);
                setOrders(response.data.data);
            } catch (error) {
                console.error('Failed to load orders', error);
                // Fallback to test data if API call fails
                // const transformedData = transformOrderData(testData.data.data);
                // setTableData(transformedData);
                // setOrders(testData.data.data);
            } finally {
                setLoading(false);
            }
        };

        loadOrders();
    }, [filters, reloadTick]); // Add filters as a dependency

    // Function to transform order data to match the expected format for OrderList
    const transformOrderData = (orders: any) => {
        console.log(orders.data, ' here is the orders');
        // Get the columns from the original dataTableData
        const columns = dataTableData.columns;
        
        // Filter orders based on the current filters
        let filteredOrders = orders.data;
        if (filters.brand) {
            filteredOrders = filteredOrders.filter((order: any) => 
                (order.items ?? []).some((item: any) =>
                    String(item.product?.brand ?? '').trim().toLowerCase() === String(filters.brand).trim().toLowerCase())
            );
        }
        if (filters.status) {
            filteredOrders = filteredOrders.filter((order: any) =>
                shownStatus(order).toLowerCase() === String(filters.status).toLowerCase()
            );
        }
        if (filters.orderId) {
            filteredOrders = filteredOrders.filter((order: any) =>
                String(order.id ?? '').toLowerCase().includes(String(filters.orderId).trim().replace('#', '').toLowerCase())
            );
        }
        if (filters.sku) {
            filteredOrders = filteredOrders.filter((order: any) =>
                (order.items ?? []).some((item: any) =>
                    (item.variant?.skuCode ?? '').toString().toLowerCase().includes(String(filters.sku).toLowerCase())
                )
            );
        }
        
        // Transform the filtered orders to match the expected row format.
        // Null-safe: a malformed sub-order must not crash the whole table.
        const rows = (filteredOrders || []).map((order: any) => {
            const item = (order.items && order.items[0]) || {};
            const addr = order.address || {};
            // Sum the sub-order line totals when multi-item; item.totalPrice
            // is already quantity-multiplied on the backend.
            const subOrderTotal = (order.items ?? []).reduce(
                (sum: number, it: any) => sum + Number(it.totalPrice ?? (it.unitPrice ?? 0) * (it.quantity ?? 1)),
                0
            );
            const orderDate = order.createdAt
                ? new Date(order.createdAt).toLocaleDateString('en-IN', {
                      day: '2-digit', month: 'short', year: 'numeric',
                  })
                : '-';
            return {
                orderId: order.id,
                // Who placed the order — name (+ mobile) from the delivery address.
                customer: addr.fullName
                    ? `${addr.fullName}${addr.mobile ? ' · ' + addr.mobile : ''}`
                    : '-',
                productName: (order.items ?? []).length > 1
                    ? [`${item.product?.productName ?? '-'} +${(order.items.length - 1)} more`]
                    : [item.product?.productName ?? '-'],
                // Show the merchant-set SKU code, not the Mongo ObjectId.
                productSku: item.variant?.skuCode ?? '-',
                // Line-1 quantity (multi-item sub-orders show + more above).
                qty: item.quantity ?? '-',
                mrp: `Rs. ${item.unitPrice ?? 0}`,
                // Sub-order total: sum of items' totalPrice (quantity-adjusted).
                total: `Rs. ${subOrderTotal}`,
                // deliveryCharge is currently null on SubOrder (backend gap),
                // so show "—" instead of lying with "FREE" / "Rs. 0".
                delivery: order.deliveryCharge != null ? `Rs. ${order.deliveryCharge}` : '—',
                orderDate,
                // The shipment lifecycle (NEW -> IN TRANSIT -> Delivered) lives on
                // the SUB-ORDER: that is what the Shiprocket delivery webhook
                // updates. Individual items keep their own status ("Confirmed")
                // and are only moved for exceptions like cancel/return — reading
                // item.status first meant every row showed "Confirmed" forever.
                // Prefer the sub-order status, but let an item exception win so a
                // cancelled/returned line is still visible.
                status: shownStatus(order),
                // Action cell needs the sub-order id + shipment id so it can
                // offer live Shiprocket tracking once a shipment exists.
                action: {
                    order,
                    onDone: () => setReloadTick((n) => n + 1),
                    subOrderId: order.id,
                    status: order.status,
                    shipmentId: order.shiprocketShipmentId ?? null,
                },
                // Shiprocket's shipping label and tax invoice PDFs, once the parcel is booked.
                label: order.shippingLabelUrl ?? null,
                invoice: order.shippingInvoiceUrl ?? null,
                mode: order.paymentMode ?? '-',
                shipmentId: order.shiprocketShipmentId ?? '-',
                trackingId: order.shiprocketShipmentId ?? '-'
            };
        });
        
        return {
            columns,
            rows
        };
    };

    const handleFilterChange = (newFilters: any) => {
        setFilters(newFilters);
    };

    return (
        <>
            <FaiBox className={"order"}>
                <FaiTypography variant={"h4"} fontWeight={typography.fontWeightMedium} mb={"21px"}>
                    Orders
                </FaiTypography>
                <FaiTypography variant={"body2"} sx={{ fontWeight: `${typography.fontWeightMedium} !important` }} mb={"21px"}>
                    Search Product
                </FaiTypography>
                {(() => {
                    const waiting = ((orders as any)?.data ?? []).filter((o: any) => nextStep(o) === 'approve' || nextStep(o) === 'ship').length;
                    return waiting > 0 ? (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <b>{waiting === 1 ? '1 order needs' : `${waiting} orders need`} your action.</b> {READY_TO_SHIP_REMINDER}
                        </Alert>
                    ) : null;
                })()}
                <FaiBox className={"inventory-content"}>
                    <FaiBox className={"inventory-content-header"}>
                        <HeaderBar
                            onFilterChange={handleFilterChange}
                            statuses={Array.from(new Set((orders as any)?.data?.map((o: any) => shownStatus(o)).filter((x: string) => x && x !== '-') ?? [])) as string[]}
                        />
                    </FaiBox>
                    <FaiBox className={"inventory-content-body"} mt={3}>
                        {loading ? (
                            <FaiTypography>Loading orders...</FaiTypography>
                        ) : (
                            <OrderList data={tableData || dataTableData} />
                        )}
                    </FaiBox>
                </FaiBox>
            </FaiBox>
        </>
    );
}

export default Orders;
